import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  AlertTriangle,
  Activity,
  BarChart3,
  Brain,
  CheckCircle2,
  ChevronDown,
  DoorOpen,
  Flag,
  Gauge,
  Handshake,
  Lightbulb,
  LineChart,
  MessageSquare,
  Mic,
  Presentation,
  Search,
  ShieldAlert,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Users,
  Video,
  Zap,
  Loader2,
  X,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import MeetingPDFReport from '@/components/MeetingPDFReport';
import { toast } from 'react-toastify';
import { getMeetingById, getAdminMeetingById } from '@/api/meetingService';
import { API_BASE_URL } from '@/config/api.enpoints';
import useAuthStore from '@/store/useAuthStore';
import { useDashboardTheme } from './themeHelper';
import PracticeGrowthView from './PracticeGrowthView';
import { useMeetingPracticeComparison } from '@/hooks/UseMeeting';
import {
  computeConversationAnalytics,
  formatMonologueDuration,
  mapApiConversationAnalytics,
} from '@/utils/conversationAnalytics';

// ─── Types ───────────────────────────────────────────────────────────────────

type Word = { word: string; punctuated_word?: string; start: number; end: number };
type TranscriptSegment = {
  speaker: string | number; start: number; end: number;
  transcript?: string; text?: string; words?: Word[];
};

type HowToImproveItem = { title: string; description: string };

type MeetingDetail = {
  id: string;
  isScenario: boolean;
  isPitch: boolean;
  isDigitalTwin: boolean;
  hasNewFormatData: boolean;
  salesRep: string;
  company: string;
  industry: string;
  businessModel: string;
  techStack: string;
  decisionProcess: string;
  competitorsInvolved: string;
  meetingType: string;
  status: string;
  overallScore: number;
  createdAt: string;
  audioUrl: string;
  audioDownloadUrl?: string;
  rawAudioUrl: string;
  transcript_text: string;
  transcriptJson: TranscriptSegment[];
  hasTiming: boolean;
  customerContact: { firstName: string; lastName: string; email: string; fullName: string; phone?: string } | null;
  personaDescriptors: string[];
  meetingPurpose: string[];
  meetingObjective: string[];
  otherDetails?: Record<string, any>;
  aiData: {
    summary: string[];
    insights: string[];
    suggestions: string[];
    justification: string[];
    videoAnalysis: string[];
    coachingInsights: {
      wentWell: string[];
      needsImprovement: string[];
      howToImprove: HowToImproveItem[];
      aiCoachingSummary: string;
    };
    objectionsDetected: { type: string; status: string }[];
    objectionFeedback: string;
    buyingSignalsDetected: string[];
    buyingSignalInsight: string;
    missedOpportunities: string[];
    missedOpportunityAdvice: string;
    dealIntelligence: {
      riskLevel: string;
      riskReason: string;
      closeProbability: number;
      opportunityProgression: string;
    };
    liveAudioFeedback: { negotiation: string[]; objection: string[]; demo: string[] };
    energyLevel: string;
    interruptionsCount: number;
  };
  meetingNotes?: {
    title?: string;
    executiveSummary?: string;
    keyDiscussionPoints?: { topic?: string; details?: string }[];
    decisionsMade?: string[];
    actionItems?: { task?: string; owner?: string; priority?: string }[];
    nextSteps?: string[];
    openQuestions?: string[];
  } | null;
  aiScore: { overall: number };
  scoringRaw: Record<string, any>;
  conversationAnalyticsRaw: any;
  talkRatio: { agent: number; customer: number };
  duration: number;
  legacyMongoId?: string;
  hasPracticeSessions?: boolean;
  practiceSessionCount?: number;
};

// ─── Small format helpers ─────────────────────────────────────────────────────

const formatWords = (raw: string) =>
  String(raw || '')
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

const normalizeTime = (value: any, sibling?: any): number => {
  const n = typeof value === 'number' ? value : Number(value);
  if (typeof n !== 'number' || isNaN(n)) return 0;
  const companion = typeof sibling === 'number' ? sibling : Number(sibling);
  if ((companion && companion > 1000) || n > 1000) return n / 1000;
  return n;
};

const fmt = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

// "user" -> Rep, "assistant" -> Buyer (roleplay transcripts), else diarized "Speaker N"
const normSpeaker = (raw: string | number): string => {
  const s = String(raw).trim();
  const low = s.toLowerCase();
  if (low === 'user') return 'Rep';
  if (low === 'assistant') return 'Buyer';
  if (/^speaker\s+\d+$/i.test(s)) return s.charAt(0).toUpperCase() + s.slice(1);
  const collapsed = s.replace(/^(Speaker\s+)+/i, '');
  if (/^\d+$/.test(collapsed)) return `Speaker ${parseInt(collapsed, 10) + (collapsed === '0' ? 1 : 0)}`;
  if (/^\d+$/.test(s)) return `Speaker ${parseInt(s, 10) === 0 ? 1 : parseInt(s, 10)}`;
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Speaker';
};

const normalizeTranscriptSegment = (item: any): TranscriptSegment => {
  const rawStart = item.start ?? item.st ?? 0;
  const rawEnd = item.end ?? item.en ?? rawStart;
  const s = normalizeTime(rawStart, rawEnd);
  const e = normalizeTime(rawEnd, rawStart);
  const [start, end] = e < s ? [e, s] : [s, e];
  return {
    speaker: normSpeaker(item.speaker ?? 0),
    start, end,
    transcript: (item.transcript || item.text || '').trim(),
    words: (item.words || []).map((w: any) => ({
      word: w.word || '',
      punctuated_word: w.punctuated_word,
      start: normalizeTime(w.start, rawEnd),
      end: normalizeTime(w.end, rawEnd),
    })),
  };
};

const dedupeTranscriptSegments = (segments: TranscriptSegment[]): TranscriptSegment[] => {
  const result: TranscriptSegment[] = [];
  const seen = new Set<string>();
  for (const segment of segments) {
    if (!segment.transcript) continue;
    if (segment.end <= segment.start) continue;
    const key = `${segment.speaker}||${segment.start.toFixed(2)}||${segment.end.toFixed(2)}||${segment.transcript}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const last = result[result.length - 1];
    if (last && last.speaker === segment.speaker && last.transcript === segment.transcript && segment.start <= last.end + 0.1) {
      last.end = Math.max(last.end, segment.end);
      if (segment.words?.length) last.words = [...(last.words || []), ...segment.words];
      continue;
    }
    result.push({ ...segment });
  }
  return result;
};

// Handles 3 shapes: word-level timed segments (real recordings), simple role/text
// arrays (pitch/scenario roleplay), and plain "SPEAKER: text" strings.
const parseGenericTranscript = (raw: any): { segments: TranscriptSegment[]; hasTiming: boolean } => {
  const tj = raw.transcriptJson;
  if (Array.isArray(tj) && tj.length) {
    const first = tj.find((it: any) => it && typeof it === 'object');
    const wordLevel = first && first.speaker !== undefined &&
      (typeof first.start === 'number' || typeof first.end === 'number' || Array.isArray(first.words));
    if (wordLevel) {
      const segs = dedupeTranscriptSegments(tj.map(normalizeTranscriptSegment));
      if (segs.length) return { segments: segs, hasTiming: true };
    }
    const simple = tj.filter((it: any) =>
      it && (it.role || it.speaker) && (it.text || it.content || it.transcript) &&
      String(it.role || '').toLowerCase() !== 'system'
    );
    if (simple.length) {
      const segs: TranscriptSegment[] = simple.map((it: any, idx: number) => ({
        speaker: normSpeaker(it.role || it.speaker || (idx % 2 === 0 ? 'user' : 'assistant')),
        start: idx, end: idx + 1,
        transcript: String(it.text || it.content || it.transcript || '').trim(),
        words: [],
      }));
      return { segments: segs, hasTiming: false };
    }
  }
  const plain: string = raw.transcript || raw.transcript_text || '';
  if (plain.trim()) {
    const parts = plain.split(/\n\n+/).map((p: string) => p.trim()).filter(Boolean);
    const segs: TranscriptSegment[] = [];
    parts.forEach((part: string, idx: number) => {
      const m = part.match(/^([A-Za-z][A-Za-z0-9_ ]{0,20}):\s*([\s\S]*)$/);
      if (m) segs.push({ speaker: normSpeaker(m[1]), start: idx, end: idx + 1, transcript: m[2].trim(), words: [] });
      else segs.push({ speaker: 'Speaker', start: idx, end: idx + 1, transcript: part, words: [] });
    });
    return { segments: segs, hasTiming: false };
  }
  return { segments: [], hasTiming: false };
};

const computeTalkRatio = (segments: TranscriptSegment[], rawTalkRatio: { agent: number; customer: number }) => {
  if (!segments.length) return { ...rawTalkRatio };
  const speakerTimes: Record<string, number> = {};
  for (const seg of segments) {
    const sp = String(seg.speaker);
    speakerTimes[sp] = (speakerTimes[sp] || 0) + Math.max(0, seg.end - seg.start);
  }
  const total = Object.values(speakerTimes).reduce((a, b) => a + b, 0);
  if (total === 0) return { ...rawTalkRatio };
  const speakers = Object.keys(speakerTimes);
  if (speakers.length === 1) return { agent: 100, customer: 0 };
  const agentSp = speakers[0];
  const agentPct = Math.round((speakerTimes[agentSp] / total) * 100);
  return { agent: agentPct, customer: 100 - agentPct };
};

// ─── Scoring fallback chain (mirrors RecreateWithAI) ─────────────────────────

const transform = (raw: any): MeetingDetail => {
  const scenario = raw.scenario;
  const scenarioType = String(raw.scenario?.meetingType || raw.meetingType || raw.pitchType || raw.otherDetails?.meetingType || '').toLowerCase();
  const hasScenarioObject = !!scenario || raw.isScenario === true || Boolean(raw.scenario?.id) || Boolean(raw.scenario?.title);
  const isScenario = hasScenarioObject || scenarioType === 'scenario';
  const titleOrType = String(raw.pitchTitle || raw.otherDetails?.meetingType || raw.meetingType || raw.pitchType || '').toLowerCase();
  const isDigitalTwin = !isScenario && (titleOrType.includes('digital twin') || titleOrType.includes('digital-twin'));
  const isMeeting = raw.source === 'meeting' || Boolean(raw.transcript_text || raw.transcriptJson || raw.uploaded_audio_url || raw.s3Url || raw.meeting_type || raw.meetingNotes || String(raw.legacyMongoId || '').startsWith('cal-') || String(raw.legacyMongoId || '').startsWith('live-'));
  const isPitch = !isScenario && !isDigitalTwin && !isMeeting && Boolean(raw.pitchTitle || raw.pitchType);
  const feedback = raw.feedback;

  const scoringData: Record<string, any> | undefined =
    (raw.ai_score && Object.keys(raw.ai_score).length > 0 ? raw.ai_score : undefined) ??
    raw.scoring ??
    raw.categoryScores?.scores ??
    feedback?.scores ??
    raw.ai_summary?.scores ??
    raw.aiFeedback?.scores;

  let source: any = null;
  const summarySource = raw.ai_summary ?? raw.aiFeedback;
  if (summarySource) {
    if (typeof summarySource === 'string') {
      try { source = JSON.parse(summarySource); } catch { source = null; }
    } else if (Array.isArray(summarySource)) {
      source = { summary: summarySource, insights: [], suggestions: [], justification: [] };
    } else if (typeof summarySource === 'object') {
      source = summarySource;
    }
  }

  const isEmptySummary = (s: any): boolean =>
    !s || (!s.summary?.length && !s.insights?.length && !s.suggestions?.length &&
      !s.coachingInsights && !s.dealIntelligence && !s.buyingSignalsDetected?.length &&
      !s.objectionsDetected?.length && !s.missedOpportunities?.length);

  const DEFAULT_SUMMARY = { summary: [], insights: [], suggestions: [], justification: [] };
  const sum: any = !isEmptySummary(source) ? source : (feedback ?? source ?? DEFAULT_SUMMARY);

  sum.summary = sum.summary?.length ? sum.summary : (Array.isArray(raw.summary) ? raw.summary : feedback?.summary || []);
  sum.insights = sum.insights?.length ? sum.insights : (raw.insights || feedback?.insights || []);
  sum.suggestions = sum.suggestions?.length ? sum.suggestions : (raw.suggestions || feedback?.suggestions || []);
  sum.justification = sum.justification?.length
    ? sum.justification
    : (raw.categoryScores?.justification || raw.ai_score?.justification || feedback?.justification || []);
  sum.liveAudioFeedback = sum.liveAudioFeedback || raw.liveAudioFeedback || raw.ai_summary?.liveAudioFeedback || raw.aiFeedback?.liveAudioFeedback;
  sum.videoAnalysis = sum.videoAnalysis || feedback?.videoAnalysis || raw.videoAnalysis || raw.ai_summary?.videoAnalysis || raw.ai_summary?.video_analysis || raw.aiFeedback?.videoAnalysis || raw.aiFeedback?.video_analysis;

  const hasNewFormatData = Boolean(
    // Only treat as "new format" when explicit new-format fields are present
    // (buying signals, objections, coaching, deal intelligence, missed opportunities)
    sum.buyingSignalsDetected?.length ||
    sum.objectionsDetected?.length ||
    sum.coachingInsights ||
    sum.dealIntelligence ||
    sum.missedOpportunities?.length
  );

  const overall =
    (typeof scoringData?.overall_percentange === 'number' && scoringData.overall_percentange > 0
      ? scoringData.overall_percentange
      : typeof raw.overallScore === 'number'
        ? raw.overallScore
        : typeof raw.score === 'number'
          ? raw.score
          : typeof raw.overallPercentage === 'number'
            ? raw.overallPercentage
            : 0);

  const prof = raw.TargetCompanyProfile || {};
  const cc = raw.customerContact || raw.companyContact || null;
  const company = isScenario ? (scenario?.title || '') : (prof.companyName || raw.companyName || raw.customerData?.companyName || '');
  const industry = isScenario ? (scenario?.industry || '') : (prof.industry || raw.customerData?.industry || '');
  const businessModel = isScenario ? (scenario?.difficulty || '') : (prof.businessModel || '');

  const getAudioRawFromObject = (obj: any): string => {
    if (!obj || typeof obj !== 'object') return '';

    const rawUrl =
      obj.uploaded_audio_url ||
      obj.uploadedAudio ||
      obj.raw?.uploaded_audio_url ||
      obj.details?.uploaded_audio_url ||
      obj.otherDetails?.uploaded_audio_url ||
      obj.recordingUrl ||
      obj.recording_url ||
      obj.s3Url ||
      obj.audioUrl ||
      obj.audio_url ||
      (Array.isArray(obj.fileUrls) ? obj.fileUrls[0] : null) ||
      (typeof obj.recording === 'string' ? obj.recording : (obj.recording?.url || obj.recording?.s3Url || obj.recording?.location)) ||
      (typeof obj.audioFile === 'string' ? obj.audioFile : (obj.audioFile?.url || obj.audioFile?.location)) ||
      obj.audio;

    return (rawUrl && typeof rawUrl === 'string') ? rawUrl.trim() : '';
  };

  const audioRaw = getAudioRawFromObject(raw);

  const formatAudioUrl = (urlVal: any) => {
    if (!urlVal) return '';
    let trimmed = String(urlVal).trim().replace(/\\/g, '/');
    if (!trimmed) return '';

    while (
      trimmed.includes('stream-audio?key=http') ||
      trimmed.includes('stream-audio?key=%68%74%74%70') ||
      trimmed.includes('stream-audio?key=%48%54%54%50')
    ) {
      try {
        const urlObj = new window.URL(trimmed);
        const nested = urlObj.searchParams.get('key');
        if (!nested) break;
        trimmed = decodeURIComponent(nested);
      } catch {
        break;
      }
    }

    if (trimmed.includes('localhost:5000/v1/meeting/stream-audio')) {
      trimmed = trimmed.replace('http://localhost:5000', API_BASE_URL);
    }

    if (
      trimmed.startsWith('http://') ||
      trimmed.startsWith('https://') ||
      trimmed.startsWith('blob:') ||
      trimmed.startsWith('data:')
    ) {
      if (trimmed.includes('.blob.core.windows.net/')) {
        return `${API_BASE_URL}/v1/meeting/stream-audio?key=${encodeURIComponent(trimmed)}`;
      }
      return trimmed;
    }
    const cleanPath = trimmed.replace(/^\/+/, '').replace(/^uploads\//, '');
    return `${API_BASE_URL}/v1/meeting/stream-audio?key=${encodeURIComponent(cleanPath)}`;
  };

  const audioUrl = formatAudioUrl(audioRaw);

  const audioDownloadRaw =
    raw.uploaded_audio_download_url ||
    raw.audioDownloadUrl ||
    raw.downloadUrl ||
    audioRaw;
  const audioDownloadUrl = formatAudioUrl(audioDownloadRaw);

  const { segments, hasTiming } = parseGenericTranscript(raw);
  const transcriptText = raw.transcript_text || raw.transcript || '';

  const meetingTypeRaw = isScenario
    ? (scenario?.meetingType || 'scenario')
    : (raw.otherDetails?.meetingType || raw.pitchTitle || raw.meetingType || 'call');

  return {
    id: raw.id || raw._id || raw.meetingId || raw.legacyMongoId || raw.sessionId || '',
    isScenario,
    isPitch,
    isDigitalTwin,
    hasNewFormatData,
    salesRep: raw.User?.name || raw.user?.name || 'Unknown',
    company, industry, businessModel,
    techStack: prof.techStack || '',
    decisionProcess: prof.decisionProcess || '',
    competitorsInvolved: prof.competitorsInvolved || '',
    meetingType: String(meetingTypeRaw).toLowerCase(),
    status: raw.status ? raw.status.charAt(0).toUpperCase() + raw.status.slice(1).toLowerCase() : 'Active',
    overallScore: overall,
    createdAt: raw.createdAt || raw.created_at || raw.sessionStart || '',
    audioUrl,
    audioDownloadUrl,
    rawAudioUrl: audioRaw,
    transcript_text: transcriptText,
    transcriptJson: segments,
    hasTiming,
    customerContact: isScenario
      ? (scenario?.personaTitle ? { firstName: '', lastName: '', email: '', fullName: scenario.personaTitle, phone: '' } : null)
      : (cc ? {
        firstName: cc.firstName || '', lastName: cc.lastName || '',
        email: cc.email || '', fullName: cc.fullName || `${cc.firstName || ''} ${cc.lastName || ''}`.trim(),
        phone: cc.phoneNumber || '',
      } : null),
    personaDescriptors: isScenario
      ? [scenario?.personaStyle].filter(Boolean)
      : (raw.companyContact?.buyerPersona || []),
    meetingPurpose: raw.otherDetails?.meetingPurpose || [],
    meetingObjective: raw.otherDetails?.meetingObjective || [],
    otherDetails: raw.otherDetails || {},
    aiData: {
      summary: Array.isArray(sum.summary) ? sum.summary : [],
      insights: Array.isArray(sum.insights) ? sum.insights : [],
      suggestions: Array.isArray(sum.suggestions) ? sum.suggestions : [],
      justification: Array.isArray(sum.justification) ? sum.justification : [],
      videoAnalysis: Array.isArray(sum.videoAnalysis)
        ? sum.videoAnalysis
        : (typeof sum.videoAnalysis === 'string' ? [sum.videoAnalysis] : []),
      coachingInsights: {
        wentWell: Array.isArray(sum.coachingInsights?.wentWell) ? sum.coachingInsights.wentWell : [],
        needsImprovement: Array.isArray(sum.coachingInsights?.needsImprovement) ? sum.coachingInsights.needsImprovement : [],
        howToImprove: Array.isArray(sum.coachingInsights?.howToImprove) ? sum.coachingInsights.howToImprove : [],
        aiCoachingSummary: sum.coachingInsights?.aiCoachingSummary || '',
      },
      objectionsDetected: Array.isArray(sum.objectionsDetected) ? sum.objectionsDetected : [],
      objectionFeedback: sum.objectionFeedback || '',
      buyingSignalsDetected: Array.isArray(sum.buyingSignalsDetected) ? sum.buyingSignalsDetected : [],
      buyingSignalInsight: sum.buyingSignalInsight || '',
      missedOpportunities: Array.isArray(sum.missedOpportunities) ? sum.missedOpportunities : [],
      missedOpportunityAdvice: sum.missedOpportunityAdvice || '',
      dealIntelligence: {
        riskLevel: sum.dealIntelligence?.riskLevel || '',
        riskReason: sum.dealIntelligence?.riskReason || '',
        closeProbability: sum.dealIntelligence?.closeProbability || 0,
        opportunityProgression: sum.dealIntelligence?.opportunityProgression || '',
      },
      liveAudioFeedback: {
        negotiation: Array.isArray(sum.liveAudioFeedback?.negotiation) ? sum.liveAudioFeedback.negotiation : [],
        objection: Array.isArray(sum.liveAudioFeedback?.objection) ? sum.liveAudioFeedback.objection : [],
        demo: Array.isArray(sum.liveAudioFeedback?.demo) ? sum.liveAudioFeedback.demo : [],
      },
      energyLevel: sum.energyLevel || raw.conversationAnalytics?.energy_level || 'N/A',
      interruptionsCount: typeof sum.interruptionsCount === 'number'
        ? sum.interruptionsCount
        : (raw.conversationAnalytics?.interruptions ?? 0),
    },
    meetingNotes: (() => {
      const rawNotes = raw.meetingNotes || sum.meetingNotes || raw.ai_summary?.meetingNotes || raw.aiFeedback?.meetingNotes;
      if (!rawNotes) return null;
      if (typeof rawNotes === 'string') {
        try { return JSON.parse(rawNotes); } catch { return { executiveSummary: rawNotes }; }
      }
      return typeof rawNotes === 'object' ? rawNotes : null;
    })(),
    aiScore: { overall },
    scoringRaw: scoringData || {},
    conversationAnalyticsRaw: raw.conversationAnalytics || raw.conversation_analytics || null,
    talkRatio: { agent: raw.talkRatio?.agent || 60, customer: raw.talkRatio?.customer || 40 },
    duration: typeof raw.duration === 'number'
      ? raw.duration
      : typeof raw.conversationAnalytics?.total_duration_seconds === 'number'
        ? raw.conversationAnalytics.total_duration_seconds
        : typeof raw.conversationAnalyticsRaw?.total_duration_seconds === 'number'
          ? raw.conversationAnalyticsRaw.total_duration_seconds
          : 0,
    legacyMongoId: raw.legacyMongoId || '',
    hasPracticeSessions: raw.hasPracticeSessions,
    practiceSessionCount: raw.practiceSessionCount,
  };
};

// ─── Single-theme color helpers (matches app's RecreateWithAI palette) ───────

const ACCENT = '#B8F890';

const getSemanticColor = (score: number, isDark: boolean = true) => {
  if (score >= 75) return isDark ? '#B8F890' : '#5a9d50';
  if (score >= 50) return isDark ? '#F5B84C' : '#d97706';
  return isDark ? '#F17272' : '#dc2626';
};

const getStatusLabel = (score: number) => {
  if (score >= 85) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Average';
  return 'Needs Work';
};

const getRiskMeta = (risk?: string, isDark: boolean = true) => {
  const r = risk?.toLowerCase();
  if (r === 'low') return { color: isDark ? '#B8F890' : '#5a9d50', width: 33, desc: 'Deal is healthy with low risk.' };
  if (r === 'high') return { color: isDark ? '#F17272' : '#dc2626', width: 100, desc: 'High risk — needs immediate attention.' };
  return { color: isDark ? '#F5B84C' : '#d97706', width: 66, desc: 'Some risk present. Address to improve outcome.' };
};

const getProgressionMeta = (progression?: string, isDark: boolean = true) => {
  const p = progression?.toLowerCase();
  if (p === 'high') return { color: isDark ? '#B8F890' : '#5a9d50', width: 100, desc: 'Strong progress toward closing.' };
  if (p === 'low') return { color: isDark ? '#F17272' : '#dc2626', width: 33, desc: 'Limited movement toward next stage.' };
  return { color: isDark ? '#F5B84C' : '#d97706', width: 66, desc: 'Steady progress toward next stage.' };
};

// ─── Category meta for Performance Scores (mirrors RecreateWithAI) ───────────

const CATEGORY_ORDER = [
  'opening', 'discovery', 'pitch_delivery', 'engagement',
  'confidence_and_tone', 'closing', 'digital_twin_video_analytics', 'overall_impression',
];

const CATEGORY_META: Record<string, { icon: React.ComponentType<{ className?: string }>; label: string }> = {
  opening: { icon: DoorOpen, label: 'Opening' },
  discovery: { icon: Search, label: 'Discovery' },
  pitch_delivery: { icon: Presentation, label: 'Pitch Delivery' },
  engagement: { icon: Handshake, label: 'Engagement' },
  confidence_and_tone: { icon: TrendingUp, label: 'Confidence & Tone' },
  closing: { icon: Flag, label: 'Closing' },
  overall_impression: { icon: Star, label: 'Overall Impression' },
  digital_twin_video_analytics: { icon: Video, label: 'Video Analytics' },
};

const getCategoryScoreOutOf100 = (scores: Record<string, unknown>) => {
  const values = Object.values(scores).filter((v) => typeof v === 'number') as number[];
  if (!values.length) return 0;
  const sum = values.reduce((a, b) => a + b, 0);
  return Math.round((sum / (values.length * 5)) * 100);
};

// ─── Reusable expandable list (view more / view less) ────────────────────────

function ExpandableList<T>({
  items, renderItem, previewCount = 2, children, title = 'Details'
}: {
  items: T[];
  renderItem: (item: T, i: number) => React.ReactNode;
  previewCount?: number;
  children?: React.ReactNode;
  title?: string;
}) {
  const { isDark } = useDashboardTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const remaining = Math.max(0, items.length - previewCount);
  const hasMore = remaining > 0 || (items.length > 0 && !!children);
  const visibleItems = items.slice(0, previewCount);

  return (
    <div className="flex flex-col flex-1">
      {visibleItems.length > 0 && (
        <div className="space-y-2">
          {visibleItems.map((item, i) => renderItem(item, i))}
        </div>
      )}
      {/* If no list items, show children (advice/insight) directly on card */}
      {items.length === 0 && children && (
        <div className="text-md text-white/80 leading-relaxed">
          {children}
        </div>
      )}
      {hasMore && (
        <div className="mt-3 flex items-center">
          <button
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-1 text-sm font-semibold hover:opacity-80 transition-opacity cursor-pointer p-0 bg-transparent border-0"
            style={{
              color: isDark ? '#B8F890' : '#5a9d50',
            }}
          >
            <span>{remaining > 0 ? `View More (${remaining})` : 'View More'}</span>
            <span style={{ fontSize: 13, lineHeight: 1 }}>→</span>
          </button>
        </div>
      )}

      {modalOpen && (
        <Dialog
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            style: {
              backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
              color: isDark ? '#ffffff' : '#000000',
              borderRadius: '16px',
            },
          }}
        >
          <DialogTitle className={`flex justify-between items-center border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            <span className="text-xl font-bold">{title}</span>
            <IconButton onClick={() => setModalOpen(false)} size="small" sx={{ color: isDark ? 'white' : 'inherit' }}>
              <X className="w-5 h-5" />
            </IconButton>
          </DialogTitle>
          <DialogContent className="pt-4 pb-4 space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="space-y-2">{items.map((item, i) => renderItem(item, i))}</div>
            {children && (
              <div className="mt-3 pt-3 border-t border-white/10 text-white/85 leading-relaxed text-sm">
                {children}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const BulletListWithToggle: React.FC<{ title: string; items: string[]; icon?: React.ReactNode; previewCount?: number }> = ({
  title, items, icon, previewCount = 3,
}) => {
  const { isDark, colors } = useDashboardTheme();
  const accent = isDark ? '#B8F890' : '#5a9d50';
  if (!items?.length) return null;
  return (
    <div className="rounded-[16px] p-5 space-y-3" style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.text }}>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: isDark ? `${accent}1f` : '#5a9d501a' }}>
          {icon || <Video className="w-4 h-4" style={{ color: accent }} />}
        </div>
        <span className="text-xl font-semibold" style={{ color: colors.text }}>{title}</span>
      </div>
      <ExpandableList
        items={items}
        previewCount={previewCount}
        renderItem={(s, i) => (
          <div key={i} className="rounded-xl px-3 py-2 text-base" style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}`, color: colors.text }}>
            {s}
          </div>
        )}
      />
    </div>
  );
};

const SectionTitle: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => {
  const { isDark, colors } = useDashboardTheme();
  const accent = isDark ? '#B8F890' : '#5a9d50';
  return (
    <div className="flex items-center gap-2">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: isDark ? `${accent}1f` : '#5a9d501a' }}>
        {React.isValidElement(icon) ? React.cloneElement(icon as React.ReactElement<any>, { style: { color: accent } }) : icon}
      </div>
      <span className="text-lg font-semibold" style={{ color: colors.text }}>{title}</span>
    </div>
  );
};

// ─── Download Audio ───────────────────────────────────────────────────────────

const downloadAudio = async (url: string, filename: string) => {
  if (!url) {
    toast.error('No audio recording URL available.');
    return;
  }

  const cleanFilename = filename || 'recording.mp3';
  let targetUrl = url;

  if (url.includes('/uploads/') && !url.includes('download=1')) {
    const separator = url.includes('?') ? '&' : '?';
    targetUrl = `${url}${separator}download=1`;
  }

  try {
    const response = await fetch(targetUrl, { method: 'GET' });
    if (!response.ok) {
      toast.error(`Recording file not available on server (HTTP ${response.status})`);
      return;
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = blobUrl;
    a.download = cleanFilename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      if (document.body.contains(a)) document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    }, 1000);
    return;
  } catch (_err) {
    // If fetch is blocked by CORS (e.g. cross-origin S3 presigned URL), fallback to hidden iframe.
    // A hidden iframe triggers native browser download for attachments while ensuring the main window NEVER navigates away or shows 404s.
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = targetUrl;
    document.body.appendChild(iframe);
    setTimeout(() => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }, 10000);
  }
};

// ─── Score Ring ───────────────────────────────────────────────────────────────

const ScoreRing: React.FC<{ score: number; size?: number; label?: string }> = ({ score, size = 130, label }) => {
  const { isDark, colors } = useDashboardTheme();
  const sw = 9;
  const r = (size - sw * 2) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const sc = getSemanticColor(score, isDark);
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={colors.ringTrack} strokeWidth={sw} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={sc} strokeWidth={sw}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: size * 0.22, fontWeight: 700, color: sc, lineHeight: 1 }}>{score}</span>
        {label && <span style={{ fontSize: size * 0.09, color: colors.text, marginTop: 4, fontWeight: 600 }}>{label}</span>}
      </div>
    </div>
  );
};

// ─── Audio Player (single accent theme) ──────────────────────────────────────

const AudioPlayer: React.FC<{
  url: string;
  downloadUrl?: string;
  audioRef: React.RefObject<HTMLAudioElement>;
  onTimeUpdate: (t: number) => void;
  initialDuration?: number;
}> = ({ url, downloadUrl, audioRef, onTimeUpdate, initialDuration = 0 }) => {
  const { isDark, colors } = useDashboardTheme();
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(initialDuration);
  const [current, setCurrent] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [audioError, setAudioError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(url);
  const barRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);

  const shouldPlayRef = useRef(false);

  useEffect(() => {
    setCurrentSrc(url);
    setAudioError(false);
    shouldPlayRef.current = false;
  }, [url]);

  useEffect(() => {
    if (audioRef.current && currentSrc) {
      try {
        audioRef.current.load();
      } catch (_e) {
        // ignore load errors
      }
    }
  }, [currentSrc, audioRef]);

  const handleCanPlay = () => {
    if (shouldPlayRef.current && audioRef.current) {
      audioRef.current.play().then(() => {
        setPlaying(true);
      }).catch((_e) => {
        setPlaying(false);
      });
    }
  };

  const handleAudioError = useCallback(() => {
    setPlaying(false);
    setAudioError(true);
  }, []);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadAudio(downloadUrl || currentSrc || url, `recording-${Date.now()}.mp3`);
    } catch (e) {
      console.error(e);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (initialDuration > 0) {
      setDuration(initialDuration);
    }
  }, [initialDuration]);

  const resolveDuration = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    const d = el.duration;
    if (d && isFinite(d) && d > 0) {
      setDuration(d);
    } else if (initialDuration > 0) {
      setDuration(initialDuration);
    }
  }, [audioRef, initialDuration]);

  const handleTimeUpdate = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    setCurrent(el.currentTime);
    onTimeUpdate(el.currentTime);
    if (el.duration && isFinite(el.duration) && el.duration > 0) {
      setDuration(el.duration);
    }
  }, [audioRef, onTimeUpdate]);

  const toggle = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      shouldPlayRef.current = true;
      if (duration > 0 && el.currentTime >= duration - 0.5) {
        el.currentTime = 0;
        setCurrent(0);
        onTimeUpdate(0);
      }
      setPlaying(true);
      if (el.readyState === 0) {
        try { el.load(); } catch (_e) { }
      }
      const playPromise = el.play();
      if (playPromise !== undefined) {
        playPromise.catch((_err) => {
          handleAudioError();
        });
      }
    } else {
      shouldPlayRef.current = false;
      setPlaying(false);
      el.pause();
    }
  }, [audioRef, onTimeUpdate, duration, handleAudioError]);

  const scrub = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = audioRef.current; const bar = barRef.current;
    if (!el || !bar) return;
    const d = el.duration;
    if (!d || !isFinite(d) || d <= 0) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    el.currentTime = pct * d; setCurrent(pct * d); onTimeUpdate(pct * d);
  }, [audioRef, onTimeUpdate]);

  const changeSpeed = useCallback(() => {
    const el = audioRef.current;
    const speeds = [1, 1.25, 1.5, 2, 0.75];
    const idx = speeds.indexOf(speed);
    const next = speeds[(idx + 1) % speeds.length];
    setSpeed(next);
    if (el) el.playbackRate = next;
  }, [audioRef, speed]);

  const safeDuration = duration > 0 && isFinite(duration) ? duration : 0;
  const pct = safeDuration > 0 ? Math.min(100, (current / safeDuration) * 100) : 0;

  const accent = isDark ? '#B8F890' : '#5a9d50';
  const playerBg = isDark ? '#141814' : colors.surface;
  const playerBorder = isDark ? 'rgba(184,248,144,0.18)' : colors.border;
  const barBg = isDark ? '#0a0a0a' : colors.surfaceAlt;
  const barBorder = isDark ? '#2a2a2a' : colors.border;
  const waveformInactive = isDark ? '#2a2a2a' : '#cbd5e1';
  const btnBg = isDark ? '#1c1c1c' : colors.surfaceAlt;
  const btnBorder = isDark ? '#2a2a2a' : colors.border;
  const textColor = colors.text;

  const isDocument = /\.(pdf|doc|docx|ppt|pptx|txt)(?:\?|$)/i.test(url);

  if (isDocument) {
    return (
      <div style={{ background: playerBg, border: `1px solid ${playerBorder}`, borderRadius: 14, padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 15, color: textColor, fontWeight: 600 }}>Attached Document</span>
          <span style={{ fontSize: 13, color: colors.textMuted }}>No audio/video recorded</span>
        </div>
        <a href={currentSrc} target="_blank" rel="noopener noreferrer" 
           style={{ background: isDark ? '#B8F890' : '#5a9d50', border: 'none', borderRadius: 9, padding: '8px 14px', color: isDark ? '#0a1608' : '#ffffff', fontSize: 14, fontWeight: 700, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
          View Document
        </a>
      </div>
    );
  }

  return (
    <div style={{ background: playerBg, border: `1px solid ${playerBorder}`, borderRadius: 14, padding: '14px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: playing ? accent : colors.textMuted, boxShadow: playing ? `0 0 10px ${accent}` : 'none', transition: 'all 0.3s' }} />
          <span style={{ fontSize: 15, color: textColor, fontWeight: 600 }}>Session Recording</span>
        </div>
        <span style={{ fontSize: 15, color: textColor, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
          {fmt(current)} / {safeDuration > 0 ? fmt(safeDuration) : '--:--'}
        </span>
      </div>

      <div ref={barRef} onClick={scrub}
        style={{ width: '100%', height: 36, borderRadius: 8, background: barBg, border: `1px solid ${barBorder}`, cursor: 'pointer', overflow: 'hidden', position: 'relative', marginBottom: 12, display: 'flex', alignItems: 'center' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', background: `linear-gradient(90deg, ${accent}33, ${accent}0d)`, width: `${pct}%` }} />
        <div style={{ position: 'relative', zIndex: 1, width: '100%', display: 'flex', alignItems: 'center', gap: 1.5, padding: '0 10px' }}>
          {Array.from({ length: 80 }).map((_, i) => {
            const h = 25 + Math.sin(i * 0.7) * 18 + Math.sin(i * 1.8) * 10;
            return <div key={i} style={{ flex: 1, borderRadius: 2, height: `${h}%`, background: (i / 80) * 100 < pct ? accent : waveformInactive, transition: 'background 0.05s' }} />;
          })}
        </div>
        {safeDuration > 0 && <div style={{ position: 'absolute', top: 0, height: '100%', width: 2, background: accent, left: `${pct}%`, boxShadow: `0 0 8px ${accent}` }} />}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => { if (audioRef.current) audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10); }}
            style={{ background: btnBg, border: `1px solid ${btnBorder}`, borderRadius: 9, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: textColor }}>
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7M18 19l-7-7 7-7" /></svg>
          </button>
          <button onClick={toggle}
            style={{ background: isDark ? '#B8F890' : '#5a9d50', border: 'none', borderRadius: 999, width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
            {playing
              ? <svg width="14" height="14" fill={isDark ? '#0a1608' : '#ffffff'} viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" rx="1.5" /><rect x="14" y="4" width="4" height="16" rx="1.5" /></svg>
              : <svg width="14" height="14" fill={isDark ? '#0a1608' : '#ffffff'} viewBox="0 0 24 24" style={{ marginLeft: 2 }}><path d="M5 3l14 9-14 9V3z" /></svg>}
          </button>
          <button onClick={() => { if (audioRef.current) audioRef.current.currentTime = Math.min(safeDuration || 9999, audioRef.current.currentTime + 10); }}
            style={{ background: btnBg, border: `1px solid ${btnBorder}`, borderRadius: 9, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: textColor }}>
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M6 5l7 7-7 7" /></svg>
          </button>
          <button onClick={changeSpeed}
            style={{ background: isDark ? 'rgba(184, 248, 144, 0.15)' : 'rgba(90, 157, 80, 0.15)', border: `1px solid ${isDark ? 'rgba(184, 248, 144, 0.3)' : 'rgba(90, 157, 80, 0.3)'}`, borderRadius: 8, padding: '4px 11px', cursor: 'pointer', color: isDark ? '#B8F890' : '#5a9d50', fontSize: 16, fontWeight: 700, marginLeft: 4 }}>
            {speed}×
          </button>
        </div>
        <button onClick={handleDownload} disabled={downloading}
          style={{ background: isDark ? '#B8F890' : '#5a9d50', border: `1px solid ${isDark ? '#B8F890' : '#5a9d50'}`, borderRadius: 9, padding: '8px 14px', cursor: downloading ? 'not-allowed' : 'pointer', color: isDark ? '#0a1608' : '#ffffff', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, opacity: downloading ? 0.7 : 1 }}>
          {downloading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
          )}
          {downloading ? 'Downloading...' : 'Download'}
        </button>
      </div>
      {audioError && (
        <div style={{ marginTop: 10, fontSize: 13, color: '#f87171', display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(248,113,113,0.1)', padding: '6px 12px', borderRadius: 8 }}>
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>Audio file not available on server or format not supported by browser.</span>
        </div>
      )}

      <audio ref={audioRef} src={currentSrc}
        onPlay={() => { setPlaying(true); setAudioError(false); }}
        onPause={() => setPlaying(false)}
        onCanPlay={handleCanPlay}
        onError={handleAudioError}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={resolveDuration} onDurationChange={resolveDuration}
        onEnded={() => { shouldPlayRef.current = false; setPlaying(false); setCurrent(0); }}
        preload="auto" />
    </div>
  );
};

// ─── Transcript Panel (single-accent, timing-aware) ──────────────────────────

const getSpeakerColor = (sp: string, map: Map<string, string>, isDark: boolean) => {
  const pal = [isDark ? '#B8F890' : '#5a9d50', isDark ? '#94a3b8' : '#2563EB', isDark ? '#F5B84C' : '#D97706'];
  if (!map.has(sp)) map.set(sp, pal[map.size % pal.length]);
  return map.get(sp)!;
};

const TranscriptPanel: React.FC<{
  segments: TranscriptSegment[];
  transcript_text: string;
  currentTime: number;
  hasTiming: boolean;
  onSeek: (t: number) => void;
}> = ({ segments, transcript_text, currentTime, hasTiming, onSeek }) => {
  const { isDark, colors } = useDashboardTheme();
  const [search, setSearch] = useState('');
  const activeRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const userScrolling = useRef(false);
  const scrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const colorMap = useRef(new Map<string, string>());
  const prevActiveIdx = useRef(-1);

  const speakerOrder = useMemo(() => {
    const seen: string[] = [];
    segments.forEach((seg) => { const key = String(seg.speaker); if (!seen.includes(key)) seen.push(key); });
    return seen;
  }, [segments]);

  const activeIdx = useMemo(() => {
    if (!hasTiming || !segments.length) return -1;
    let lo = 0, hi = segments.length - 1, found = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const seg = segments[mid];
      if (currentTime >= seg.start && currentTime < seg.end) { found = mid; break; }
      else if (currentTime < seg.start) hi = mid - 1;
      else lo = mid + 1;
    }
    return found;
  }, [currentTime, segments, hasTiming]);

  const filtered = useMemo(() => {
    if (!search.trim()) return segments;
    const q = search.toLowerCase();
    return segments.filter((s) => (s.transcript || s.text || '').toLowerCase().includes(q));
  }, [segments, search]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const fn = () => {
      userScrolling.current = true;
      if (scrollTimer.current) clearTimeout(scrollTimer.current);
      scrollTimer.current = setTimeout(() => { userScrolling.current = false; }, 4000);
    };
    el.addEventListener('scroll', fn, { passive: true });
    return () => el.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    if (activeIdx === prevActiveIdx.current) return;
    prevActiveIdx.current = activeIdx;
    if (!userScrolling.current && activeRef.current) {
      activeRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [activeIdx]);

  if (!segments.length && transcript_text) {
    return <div style={{ padding: 24 }}><p style={{ fontSize: 17, color: colors.text, lineHeight: 1.85, whiteSpace: 'pre-wrap' }}>{transcript_text}</p></div>;
  }
  if (!segments.length) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160, color: colors.textMuted, fontSize: 17 }}>No transcript available</div>;
  }

  const q = search.toLowerCase();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: colors.surface }}>
      <div style={{ padding: '14px 18px', flexShrink: 0, borderBottom: `1px solid ${colors.border}` }}>
        <div style={{ position: 'relative' }}>
          <svg style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="14" height="14" fill="none" viewBox="0 0 24 24" stroke={colors.textMuted} strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
          </svg>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search transcript…"
            style={{ width: '100%', borderRadius: 10, border: `1px solid ${colors.border}`, background: colors.inputBg, fontSize: 16, color: colors.text, padding: '9px 12px 9px 36px', outline: 'none', boxSizing: 'border-box' }} />
        </div>
      </div>
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '12px 18px 20px' }}>
        {filtered.map((seg, idx) => {
          const label = String(seg.speaker);
          const col = getSpeakerColor(label, colorMap.current, isDark);
          const text = seg.transcript || seg.text || '';
          const isActive = hasTiming && currentTime >= seg.start && currentTime < seg.end;
          const speakerIdx = speakerOrder.indexOf(label);
          const isRight = speakerIdx % 2 !== 0;
          const prevSeg = filtered[idx - 1];
          const showLabel = idx === 0 || String(prevSeg?.speaker) !== label;

          const renderText = () => {
            if (!search.trim()) return <span style={{ fontSize: 17, lineHeight: 1.8, color: colors.text }}>{text}</span>;
            const esc = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            return (
              <span style={{ fontSize: 17, lineHeight: 1.8, color: colors.text }}>
                {text.split(new RegExp(`(${esc})`, 'gi')).map((part, i) =>
                  part.toLowerCase() === q
                    ? <mark key={i} style={{ background: '#F5B84C', color: '#0a0a0a', borderRadius: 3, padding: '0 3px', fontWeight: 700 }}>{part}</mark>
                    : <React.Fragment key={i}>{part}</React.Fragment>
                )}
              </span>
            );
          };

          return (
            <div key={`${seg.speaker}-${seg.start}-${idx}`}
              ref={isActive ? activeRef : null}
              style={{ display: 'flex', flexDirection: 'column', alignItems: isRight ? 'flex-end' : 'flex-start', marginTop: showLabel ? 16 : 5 }}>
              {showLabel && (
                <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6, color: col }}>
                  {label}
                </span>
              )}
              <div onClick={() => hasTiming && onSeek(seg.start)}
                style={{
                  maxWidth: '78%', cursor: hasTiming ? 'pointer' : 'default', position: 'relative',
                  borderLeft: !isRight ? (isActive ? `3px solid ${col}` : `2px solid ${col}60`) : 'none',
                  borderRight: isRight ? (isActive ? `3px solid ${col}` : `2px solid ${col}60`) : 'none',
                  background: isActive ? (isDark ? `${col}25` : `${col}15`) : isDark ? 'rgba(255,255,255,0.03)' : colors.surfaceAlt,
                  borderRadius: isRight ? '14px 3px 14px 14px' : '3px 14px 14px 14px',
                  padding: '11px 14px',
                  border: `1px solid ${isActive ? col : colors.border}`,
                  transition: 'all 0.2s ease',
                }}>
                {isActive && <span style={{ position: 'absolute', top: 10, right: 10, width: 7, height: 7, borderRadius: '50%', background: col }} />}
                {renderText()}
                {hasTiming && (
                  <div style={{ textAlign: isRight ? 'left' : 'right', marginTop: 5 }}>
                    <span style={{ fontSize: 14, color: colors.textMuted, fontVariantNumeric: 'tabular-nums' }}>{fmt(seg.start)}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── AI Analysis Panel — new format (deal intel / coaching / signals) ────────
// falls back to old format (summary / insights / suggestions / justification)

const AIAnalysisPanel: React.FC<{ meeting: MeetingDetail }> = ({ meeting }) => {
  const { isDark, colors } = useDashboardTheme();
  const { aiData, scoringRaw, hasNewFormatData, conversationAnalyticsRaw } = meeting;
  const showNewUi = hasNewFormatData || Boolean(conversationAnalyticsRaw);
  const [expandedImprovement, setExpandedImprovement] = useState<Set<number>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const toggleImprovement = (i: number) => {
    setExpandedImprovement((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };
  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      next.has(category) ? next.delete(category) : next.add(category);
      return next;
    });
  };

  const renderDealIntelligence = () => {
    const di = aiData.dealIntelligence;
    const overallScore = meeting.aiScore.overall;
    const sessionType = meeting.meetingType;
    const accent = isDark ? '#B8F890' : '#5a9d50';

    return (
      <div className="rounded-[16px] p-4 space-y-3" style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.text }}>
        <SectionTitle icon={<Brain className="w-4 h-4" style={{ color: accent }} />} title="Deal Intelligence" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="rounded-xl p-4 flex flex-col" style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}` }}>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
              <span className="text-sm font-semibold tracking-wide uppercase" style={{ color: colors.text }}>Overall Performance</span>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center my-3">
              <ScoreRing score={overallScore} size={140} label={getStatusLabel(overallScore)} />
            </div>
            {sessionType && (
              <div className="text-center text-md capitalize" style={{ color: colors.textMuted }}>
                {String(sessionType).replace(/_/g, ' ').toLowerCase()} session
              </div>
            )}
          </div>

          {typeof di.closeProbability === 'number' && di.closeProbability > 0 && (
            <div className="rounded-xl p-4 flex flex-col justify-between" style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}` }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: getSemanticColor(di.closeProbability, isDark) }} />
                <span className="text-sm font-semibold tracking-wide uppercase" style={{ color: colors.text }}>Close Probability</span>
              </div>
              <div className="text-3xl font-bold mb-1" style={{ color: getSemanticColor(di.closeProbability, isDark) }}>{di.closeProbability}%</div>
              <div className="h-2 w-full rounded-full overflow-hidden mb-2" style={{ backgroundColor: colors.ringTrack }}>
                <div className="h-full rounded-full" style={{ width: `${di.closeProbability}%`, backgroundColor: getSemanticColor(di.closeProbability, isDark) }} />
              </div>
              {di.riskReason && <p className="text-md leading-relaxed" style={{ color: colors.text }}>{di.riskReason}</p>}
            </div>
          )}

          {di.opportunityProgression && (
            <div className="rounded-xl p-4 flex flex-col justify-between" style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}` }}>
              <div className="flex items-center gap-2 mb-0">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: getProgressionMeta(di.opportunityProgression).color }} />
                <span className="text-sm font-semibold tracking-wide uppercase" style={{ color: colors.text }}>Opportunity Progression</span>
              </div>
              <div className="text-3xl font-bold" style={{ color: getProgressionMeta(di.opportunityProgression).color }}>{di.opportunityProgression}</div>
              <div className="h-2 w-full rounded-full overflow-hidden" style={{ backgroundColor: colors.ringTrack }}>
                <div className="h-full rounded-full" style={{ width: `${getProgressionMeta(di.opportunityProgression).width}%`, backgroundColor: getProgressionMeta(di.opportunityProgression).color }} />
              </div>
              <p className="text-md leading-relaxed" style={{ color: colors.text }}>{getProgressionMeta(di.opportunityProgression).desc}</p>
            </div>
          )}

          {di.riskLevel && (
            <div className="rounded-xl p-4 flex flex-col justify-between" style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}` }}>
              <div className="flex items-center gap-2 mb-0">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: getRiskMeta(di.riskLevel).color }} />
                <span className="text-sm font-semibold tracking-wide uppercase" style={{ color: colors.text }}>Risk Level</span>
              </div>
              <div className="text-3xl font-bold flex items-center gap-1.5" style={{ color: getRiskMeta(di.riskLevel).color }}>
                {di.riskLevel}
                {di.riskLevel.toLowerCase() !== 'low' && <AlertTriangle className="w-5 h-5" />}
              </div>
              <div className="h-2 w-full rounded-full overflow-hidden mb-2" style={{ backgroundColor: colors.ringTrack }}>
                <div className="h-full rounded-full" style={{ width: `${getRiskMeta(di.riskLevel).width}%`, backgroundColor: getRiskMeta(di.riskLevel).color }} />
              </div>
              <p className="text-md leading-relaxed" style={{ color: colors.text }}>{getRiskMeta(di.riskLevel).desc}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCoachingInsights = () => {
    const ci = aiData.coachingInsights;
    if (!ci.wentWell.length && !ci.needsImprovement.length && !ci.howToImprove.length && !ci.aiCoachingSummary) return null;
    return (
      <div className="bg-[#2525258A] rounded-[16px] p-5 text-white space-y-3">
        <SectionTitle icon={<Sparkles className="w-4 h-4 text-[#B8F890]" />} title="Pitchster Insights" />
        {ci.aiCoachingSummary && <p className="text-md text-white leading-relaxed mb-5">{ci.aiCoachingSummary}</p>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {!!ci.wentWell.length && (
            <div className="rounded-xl border border-[#52515147] bg-[#252525] p-4 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-[#B8F890]/10 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#B8F890]" />
                </div>
                <span className="text-xl font-semibold text-[#B8F890]">Went Well</span>
              </div>
              <ExpandableList title="Went Well" items={ci.wentWell} previewCount={3}
                renderItem={(item, i) => <li key={i} className="text-md text-white/80 list-disc list-inside">{item}</li>} />
            </div>
          )}
          {!!ci.needsImprovement.length && (
            <div className="rounded-xl border border-[#52515147] bg-[#252525] p-4 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-[#B8F890]/10 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-3.5 h-3.5 text-[#B8F890]" />
                </div>
                <span className="text-xl font-semibold text-[#B8F890]">Needs Improvement</span>
              </div>
              <ExpandableList title="Needs Improvement" items={ci.needsImprovement} previewCount={3}
                renderItem={(item, i) => <li key={i} className="text-md text-white/80 list-disc list-inside">{item}</li>} />
            </div>
          )}
        </div>

        {!!ci.howToImprove.length && (
          <div className="space-y-2">
            <SectionTitle icon={<Lightbulb className="w-4 h-4 text-[#B8F890]" />} title="How To Improve" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 items-start">
              {ci.howToImprove.map((item, i) => {
                const isOpen = expandedImprovement.has(i);
                return (
                  <div key={i} className="rounded-xl border border-[#52515147] bg-[#252525] p-3 space-y-1">
                    <button onClick={() => toggleImprovement(i)} className="flex items-center justify-between w-full text-left">
                      <div className="text-base font-semibold text-white">{item.title}</div>
                      <ChevronDown className={`w-4 h-4 text-white/60 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && <div className="text-base text-white/70">{item.description}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBuyingSignals = () => {
    const signals = (aiData.buyingSignalsDetected || []).filter(s => s && s.trim().length > 0);
    const insight = aiData.buyingSignalInsight?.trim();
    if (!signals.length && !insight) return null;
    return (
      <div className="bg-[#2525258A] rounded-[16px] p-4 text-white flex flex-col h-full">
        <div className="shrink-0 mb-3">
          <SectionTitle icon={<TrendingUp className="w-4 h-4 text-[#B8F890]" />} title="Buying Signals" />
        </div>
        <ExpandableList title="Buying Signals" items={signals}
          renderItem={(s, i) => <li key={i} className="rounded-xl border list-none border-[#52515147] bg-[#252525] px-3 py-2 text-base text-white/80">{s}</li>}>
          {insight && <p className="text-md text-white/80 leading-relaxed">{insight}</p>}
        </ExpandableList>
      </div>
    );
  };

  const renderObjections = () => {
    const objections = (aiData.objectionsDetected || []).filter(o => o && o.type && o.type.trim().length > 0);
    const feedback = aiData.objectionFeedback?.trim();
    if (!objections.length && !feedback) return null;
    return (
      <div className="bg-[#2525258A] rounded-[16px] p-4 text-white flex flex-col h-full">
        <div className="shrink-0 mb-3">
          <SectionTitle icon={<ShieldAlert className="w-4 h-4 text-[#B8F890]" />} title="Objections Detected" />
        </div>
        {objections.length > 0 ? (
          <ExpandableList title="Objections Detected" items={objections}
            renderItem={(o, i) => (
              <div key={i} className="rounded-xl border border-[#52515147] bg-[#252525] px-3 py-1.5 text-base flex items-center space-x-2">
                <span className="text-white/80">{o.type}</span>
                <span className="text-[#B8F890] font-semibold">{o.status}</span>
              </div>
            )}>
            {feedback && <p className="text-md text-white/80 leading-relaxed">{feedback}</p>}
          </ExpandableList>
        ) : (
          <div className="text-md text-white/80 leading-relaxed">
            {feedback && <p>{feedback}</p>}
          </div>
        )}
      </div>
    );
  };

  const renderMissedOpportunities = () => {
    const missed = (aiData.missedOpportunities || []).filter(m => m && m.trim().length > 0);
    const advice = aiData.missedOpportunityAdvice?.trim();
    if (!missed.length && !advice) return null;
    return (
      <div className="bg-[#2525258A] rounded-[16px] p-4 text-white flex flex-col h-full">
        <div className="shrink-0 mb-3">
          <SectionTitle icon={<Target className="w-4 h-4 text-[#B8F890]" />} title="Missed Opportunities" />
        </div>
        <ExpandableList title="Missed Opportunities" items={missed} previewCount={2}
          renderItem={(m, i) => <li key={i} className="rounded-xl border list-none border-[#52515147] bg-[#252525] px-3 py-2 text-base text-white/80">{m}</li>}>
          {advice && <p className="text-md text-white/80 leading-relaxed pt-1">{advice}</p>}
        </ExpandableList>
      </div>
    );
  };

  const renderScoreRing = (score: number, size = 86) => {
    const color = getSemanticColor(score, isDark);
    const circumference = 2 * Math.PI * 40;
    return (
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg viewBox="0 0 100 100" className="-rotate-90" style={{ width: size, height: size }}>
          <circle cx="50" cy="50" r="40" fill="none" stroke={colors.ringTrack} strokeWidth="9" />
          <circle cx="50" cy="50" r="40" fill="none" stroke={color} strokeWidth="9" strokeLinecap="round"
            strokeDasharray={`${(score / 100) * circumference} ${circumference}`} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold leading-none" style={{ color }}>{score}</span>
          <span className="text-[9px] text-muted mt-0.5">/100</span>
        </div>
      </div>
    );
  };

  const renderPerformanceScores = () => {
    if (!scoringRaw || !Object.keys(scoringRaw).length) return null;
    const entries = Object.entries(scoringRaw)
      .filter(([category, val]) => category !== 'overall_percentange' && category !== 'justification' && typeof val === 'object' && val !== null)
      .sort(([a], [b]) => {
        const ia = CATEGORY_ORDER.indexOf(a.toLowerCase());
        const ib = CATEGORY_ORDER.indexOf(b.toLowerCase());
        if (ia === -1 && ib === -1) return 0;
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      });
    if (!entries.length) return null;

    return (
      <div className="bg-[#2525258A] rounded-[16px] p-4 text-white space-y-3">
        <div className="flex items-center justify-between">
          <SectionTitle icon={<LineChart className="w-4 h-4 text-[#B8F890]" />} title="Performance Scores" />
          <div className="flex items-center gap-2">
            <span className="text-md text-white">Overall</span>
            <span className="text-lg font-bold" style={{ color: getSemanticColor(meeting.aiScore.overall) }}>{meeting.aiScore.overall}%</span>
            <span className="text-md font-medium text-white">{getStatusLabel(meeting.aiScore.overall)}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-start">
          {entries.map(([category, scores]) => {
            const meta = CATEGORY_META[category.toLowerCase()] || { icon: Star, label: formatWords(category) };
            const Icon = meta.icon;
            const scoreObj = scores as Record<string, unknown>;
            const score100 = getCategoryScoreOutOf100(scoreObj);
            const color = getSemanticColor(score100);
            const subMetrics = Object.entries(scoreObj).filter(([, v]) => typeof v === 'number') as [string, number][];
            const isOpen = expandedCategories.has(category);

            return (
              <div
                key={category}
                className={`rounded-xl border border-[#3a3a3a] bg-[#252525] p-3.5 flex flex-col justify-between transition-all duration-200 ${isOpen ? 'min-h-[175px] h-[175px]' : 'min-h-[56px] h-[56px]'
                  }`}
              >
                <button
                  onClick={() => toggleCategory(category)}
                  className="flex items-center justify-between gap-2 w-full text-left cursor-pointer shrink-0"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className="w-4 h-4 text-[#B8F890]" />
                    <span className="text-base font-semibold text-white capitalize truncate">{meta.label}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <p className="text-md font-semibold" style={{ color }}>{score100}%</p>
                    <ChevronDown className={`w-4 h-4 text-white/60 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                  </div>
                </button>
                {isOpen && (
                  <div className="flex items-center gap-4 flex-1 min-h-0 pt-2">
                    {renderScoreRing(score100, 76)}
                    <div className="flex-1 min-w-0 flex flex-col justify-center space-y-1.5 py-0.5">
                      {subMetrics.map(([metric, val]) => (
                        <div key={metric} className="flex items-center justify-between gap-2 text-xs">
                          <span className="text-white capitalize truncate" title={metric.replace(/_/g, ' ')}>
                            {metric.replace(/_/g, ' ')}
                          </span>
                          <span className="text-white font-semibold shrink-0">{val}/5</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderConversationAnalytics = () => {
    const apiAnalytics = meeting.conversationAnalyticsRaw ? mapApiConversationAnalytics(meeting.conversationAnalyticsRaw) : null;
    const transcriptAnalytics = meeting.hasTiming
      ? computeConversationAnalytics(meeting.transcriptJson as any, 0, aiData.interruptionsCount)
      : null;
    const analytics = apiAnalytics || transcriptAnalytics;
    if (!analytics) return null;

    const items = [
      {
        icon: Users, label: 'Talk-to-Listen Ratio', value: `${analytics.talkListenRatio.talk} / ${analytics.talkListenRatio.listen}`,
        status: (() => {
          const total = analytics.talkListenRatio.talk + analytics.talkListenRatio.listen;
          if (total === 0) return 'N/A';
          const talkPct = (analytics.talkListenRatio.talk / total) * 100;
          if (Math.abs(talkPct - 50) <= 15) return 'Good balance';
          return talkPct > 50 ? 'Rep talked more' : 'Buyer talked more';
        })()
      },
      { icon: MessageSquare, label: 'Filler Words', value: analytics.fillerWordsPerMin, unit: 'per min', status: analytics.fillerWordsPerMin <= 6 ? 'Good' : 'Could improve' },
      { icon: Activity, label: 'Longest Monologue', value: formatMonologueDuration(analytics.longestMonologueSeconds), status: analytics.longestMonologueSeconds <= 90 ? 'Good' : 'Too long' },
      { icon: Mic, label: 'Interruptions', value: analytics.interruptions, status: analytics.interruptions <= 3 ? 'Good' : 'Frequent' },
      {
        icon: Gauge, label: 'Pace', value: analytics.paceWordsPerMin, unit: 'words/min',
        status: analytics.paceWordsPerMin >= 110 && analytics.paceWordsPerMin <= 160 ? 'Optimal' : analytics.paceWordsPerMin < 110 ? 'Slow' : 'Fast'
      },
      { icon: Zap, label: 'Energy Level', value: analytics.energyLevel, status: 'Estimated' },
    ];

    return (
      <div className="bg-[#2525258A] rounded-[16px] p-4 text-white space-y-3">
        <SectionTitle icon={<BarChart3 className="w-4 h-4 text-[#B8F890]" />} title="Conversation Analytics" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {items.map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className="rounded-xl border border-[#3a3a3a] bg-[#252525] p-3 flex flex-col justify-between space-y-2">
                <div className="flex items-center gap-1.5">
                  <Icon className="w-5 h-5 text-[#B8F890] shrink-0" />
                  <span className="text-sm font-medium text-white uppercase tracking-wide leading-tight">{item.label}</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-bold text-white">{item.value}</span>
                  {item.unit && <span className="text-md text-white/40">{item.unit}</span>}
                </div>
                <span className="text-md font-medium text-[#B8F890]">{item.status}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 px-1 py-3 pb-5 space-y-4 mx-auto justify-center items-center w-full h-full overflow-auto">
      {showNewUi ? (
        <>
          {renderDealIntelligence()}
          {renderPerformanceScores()}
          {renderConversationAnalytics()}
          {renderCoachingInsights()}
          <BulletListWithToggle title="Video Analysis" items={aiData.videoAnalysis} icon={<Video className="w-4 h-4 text-[#B8F890]" />} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
            {renderBuyingSignals()}
            {renderObjections()}
            {renderMissedOpportunities()}
          </div>
        </>
      ) : (
        <>
          {renderPerformanceScores()}
          {renderConversationAnalytics()}
          <BulletListWithToggle title="Summary" items={aiData.summary} />
          <BulletListWithToggle title="Justification" items={aiData.justification} />
          <BulletListWithToggle title="Insights" items={aiData.insights} icon={<Sparkles className="w-4 h-4 text-[#B8F890]" />} />
          <BulletListWithToggle title="Suggestions" items={aiData.suggestions} icon={<Lightbulb className="w-4 h-4 text-[#B8F890]" />} />
          <BulletListWithToggle title="Video Analysis" items={aiData.videoAnalysis} icon={<Video className="w-4 h-4 text-[#B8F890]" />} />
          <BulletListWithToggle title="Call Negotiation" items={aiData.liveAudioFeedback.negotiation} icon={<Handshake className="w-4 h-4 text-[#B8F890]" />} />
          <BulletListWithToggle title="Objection Handling" items={aiData.liveAudioFeedback.objection} icon={<ShieldAlert className="w-4 h-4 text-[#B8F890]" />} />
          <BulletListWithToggle title="Demo Feedback" items={aiData.liveAudioFeedback.demo} icon={<Presentation className="w-4 h-4 text-[#B8F890]" />} />
        </>
      )}
    </div>
  );
};

// ─── Meeting Notes Panel ──────────────────────────────────────────────────────

const MeetingNotesPanel: React.FC<{ meetingNotes: any; colors: any; isDark: boolean }> = ({ meetingNotes, colors, isDark }) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  if (!meetingNotes) return null;

  const accent = isDark ? '#B8F890' : '#5a9d50';

  const toggleExpand = (sectionKey: string) => {
    setExpandedSections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const title = meetingNotes.title || '';
  const executiveSummary = meetingNotes.executiveSummary || '';
  const keyDiscussionPoints = Array.isArray(meetingNotes.keyDiscussionPoints) ? meetingNotes.keyDiscussionPoints : [];
  const actionItems = Array.isArray(meetingNotes.actionItems) ? meetingNotes.actionItems : [];
  const decisionsMade = Array.isArray(meetingNotes.decisionsMade) ? meetingNotes.decisionsMade : [];
  const nextSteps = Array.isArray(meetingNotes.nextSteps) ? meetingNotes.nextSteps : [];
  const openQuestions = Array.isArray(meetingNotes.openQuestions) ? meetingNotes.openQuestions : [];

  const cardStyle: React.CSSProperties = {
    borderRadius: 14,
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    padding: '20px 22px',
  };

  const sectionHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 16,
    fontWeight: 700,
    color: colors.text,
    marginBottom: 14,
  };

  const getPriorityBadge = (priority?: string) => {
    const p = String(priority || '').toLowerCase();
    if (p === 'high') return { bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.3)', text: '#f87171' };
    if (p === 'medium') return { bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.3)', text: '#fbbf24' };
    return { bg: isDark ? 'rgba(34, 197, 94, 0.15)' : 'rgba(90, 157, 80, 0.12)', border: isDark ? 'rgba(34, 197, 94, 0.3)' : 'rgba(90, 157, 80, 0.3)', text: isDark ? '#4ade80' : '#5a9d50' };
  };

  const renderViewMoreButton = (key: string, totalCount: number) => {
    if (totalCount <= 3) return null;
    const isExpanded = Boolean(expandedSections[key]);
    return (
      <button
        onClick={() => toggleExpand(key)}
        style={{
          background: 'transparent',
          border: 'none',
          color: accent,
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          marginTop: 10,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '4px 0',
        }}
      >
        {isExpanded ? 'View Less' : `View More (${totalCount - 3} more)`}
        <svg
          width="14"
          height="14"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    );
  };

  const visibleKeyPoints = expandedSections['keyPoints'] ? keyDiscussionPoints : keyDiscussionPoints.slice(0, 3);
  const visibleActionItems = expandedSections['actionItems'] ? actionItems : actionItems.slice(0, 3);
  const visibleDecisions = expandedSections['decisions'] ? decisionsMade : decisionsMade.slice(0, 3);
  const visibleNextSteps = expandedSections['nextSteps'] ? nextSteps : nextSteps.slice(0, 3);
  const visibleQuestions = expandedSections['questions'] ? openQuestions : openQuestions.slice(0, 3);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 24 }}>
      {/* Title Banner */}
      {title && (
        <div style={{ ...cardStyle, background: isDark ? `${accent}0d` : '#5a9d500d', borderColor: isDark ? `${accent}33` : '#5a9d5033' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ background: isDark ? `${accent}22` : '#5a9d501a', padding: 7, borderRadius: 8, color: accent, display: 'flex', flexShrink: 0 }}>
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: colors.text }}>{title}</h2>
          </div>
        </div>
      )}

      {/* Executive Summary */}
      {executiveSummary && (
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <div style={{ background: '#38bdf822', padding: 5, borderRadius: 6, color: '#38bdf8', display: 'flex' }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            Executive Summary
          </div>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, color: colors.textMuted, whiteSpace: 'pre-wrap' }}>
            {executiveSummary}
          </p>
        </div>
      )}

      {/* Key Discussion Points */}
      {keyDiscussionPoints.length > 0 && (
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <div style={{ background: '#a78bfa22', padding: 5, borderRadius: 6, color: '#a78bfa', display: 'flex' }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
            </div>
            Key Discussion Points ({keyDiscussionPoints.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {visibleKeyPoints.map((pt: any, idx: number) => {
              const topic = typeof pt === 'object' && pt !== null ? pt.topic : '';
              const details = typeof pt === 'object' && pt !== null ? pt.details : String(pt);
              return (
                <div key={idx} style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 10, padding: '14px 16px' }}>
                  {topic && (
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: accent, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent }}></span>
                      {topic}
                    </div>
                  )}
                  <div style={{ fontSize: 13.5, color: colors.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {details}
                  </div>
                </div>
              );
            })}
          </div>
          {renderViewMoreButton('keyPoints', keyDiscussionPoints.length)}
        </div>
      )}

      {/* Action Items */}
      {actionItems.length > 0 && (
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <div style={{ background: '#4ade8022', padding: 5, borderRadius: 6, color: '#4ade80', display: 'flex' }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
            </div>
            Action Items ({actionItems.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {visibleActionItems.map((item: any, idx: number) => {
              const isObj = typeof item === 'object' && item !== null;
              const task = isObj ? item.task || item.description || JSON.stringify(item) : String(item);
              const owner = isObj ? item.owner : '';
              const priority = isObj ? item.priority : '';
              const pBadge = getPriorityBadge(priority);

              return (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 10, padding: '12px 16px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, color: colors.text, fontWeight: 500, lineHeight: 1.5 }}>
                      {task}
                    </div>
                    {owner && (
                      <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
                        Owner: <strong style={{ color: colors.text }}>{owner}</strong>
                      </div>
                    )}
                  </div>
                  {priority && (
                    <span style={{ fontSize: 11, fontWeight: 700, background: pBadge.bg, border: `1px solid ${pBadge.border}`, color: pBadge.text, padding: '3px 9px', borderRadius: 6, flexShrink: 0 }}>
                      {priority}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {renderViewMoreButton('actionItems', actionItems.length)}
        </div>
      )}

      {/* Decisions Made */}
      {decisionsMade.length > 0 && (
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <div style={{ background: '#f59e0b22', padding: 5, borderRadius: 6, color: '#f59e0b', display: 'flex' }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            </div>
            Decisions Made ({decisionsMade.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {visibleDecisions.map((d: any, idx: number) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, color: colors.text, lineHeight: 1.6, background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 8, padding: '10px 14px' }}>
                <span style={{ color: '#f59e0b', marginTop: 2, flexShrink: 0 }}>✓</span>
                <span>{String(d)}</span>
              </div>
            ))}
          </div>
          {renderViewMoreButton('decisions', decisionsMade.length)}
        </div>
      )}

      {/* Next Steps */}
      {nextSteps.length > 0 && (
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <div style={{ background: '#60a5fa22', padding: 5, borderRadius: 6, color: '#60a5fa', display: 'flex' }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
            </div>
            Next Steps ({nextSteps.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {visibleNextSteps.map((step: any, idx: number) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, color: colors.text, lineHeight: 1.6, background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 8, padding: '10px 14px' }}>
                <span style={{ color: '#60a5fa', fontWeight: 700, flexShrink: 0 }}>→</span>
                <span>{String(step)}</span>
              </div>
            ))}
          </div>
          {renderViewMoreButton('nextSteps', nextSteps.length)}
        </div>
      )}

      {/* Open Questions */}
      {openQuestions.length > 0 && (
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <div style={{ background: '#f8717122', padding: 5, borderRadius: 6, color: '#f87171', display: 'flex' }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            Open Questions ({openQuestions.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {visibleQuestions.map((q: any, idx: number) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, color: colors.text, lineHeight: 1.6, background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 8, padding: '10px 14px' }}>
                <span style={{ color: '#f87171', fontWeight: 700, flexShrink: 0 }}>?</span>
                <span>{String(q)}</span>
              </div>
            ))}
          </div>
          {renderViewMoreButton('questions', openQuestions.length)}
        </div>
      )}
    </div>
  );
};

// ─── Shimmer ──────────────────────────────────────────────────────────────────

const Shimmer = () => (
  <div style={{ flex: 1, padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
    <style>{`@keyframes shimmer { 0%{opacity:0.3}50%{opacity:0.7}100%{opacity:0.3} }`}</style>
    {[52, 110, 64, 64, 300].map((h, i) => (
      <div key={i} style={{ height: h, borderRadius: 14, background: '#161616', animation: `shimmer 1.8s ease-in-out ${i * 0.1}s infinite` }} />
    ))}
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const MeetingDetailPage: React.FC = () => {
  const { isDark, colors } = useDashboardTheme();
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const role = useAuthStore((state) => state.role);
  const [meeting, setMeeting] = useState<MeetingDetail | null>(null);
  const [rawData, setRawData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'transcript' | 'ai_summary' | 'meeting_notes' | 'practice_vs_reality'>('ai_summary');
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const { data: comparisonData } = useMeetingPracticeComparison(
    meetingId,
    rawData?.practiceComparison || rawData?.ai_summary?.practiceComparison
  );

  useEffect(() => {
    if (!meeting) return;
    const hasNotes = Boolean(meeting.meetingNotes);
    const hasTrans = Boolean(meeting.transcriptJson?.length || meeting.transcript_text);
    const meetingNotesRaw = (meeting.meetingNotes || rawData?.meetingNotes) as any;
    const isCalendarMeeting = Boolean(
      String(meeting.legacyMongoId || rawData?.legacyMongoId || '').startsWith('cal-') ||
      meetingNotesRaw?.isCalendarEvent === true ||
      meetingNotesRaw?.provider === 'google' ||
      meetingNotesRaw?.provider === 'outlook' ||
      meetingNotesRaw?.provider === 'teams' ||
      meetingNotesRaw?.provider === 'microsoft' ||
      meetingNotesRaw?.provider === 'office365' ||
      String(meeting.meetingType || rawData?.meeting_type || rawData?.meetingType || '').toLowerCase().includes('teams') ||
      String(meeting.meetingType || rawData?.meeting_type || rawData?.meetingType || '').toLowerCase().includes('meet') ||
      String(meeting.meetingType || rawData?.meeting_type || rawData?.meetingType || '').toLowerCase().includes('zoom')
    );
    const hasPracticeHistory = Boolean(
      isCalendarMeeting && (
        (meeting.hasPracticeSessions === true && (meeting.practiceSessionCount ?? 1) > 0) ||
        (typeof meeting.practiceSessionCount === 'number' && meeting.practiceSessionCount > 0) ||
        (meetingNotesRaw?.hasPracticeSessions === true && (meetingNotesRaw?.practiceSessionCount ?? 1) > 0) ||
        (typeof meetingNotesRaw?.practiceSessionCount === 'number' && meetingNotesRaw.practiceSessionCount > 0) ||
        (rawData?.hasPracticeSessions === true && (rawData?.practiceSessionCount ?? 1) > 0) ||
        (typeof rawData?.practiceSessionCount === 'number' && rawData.practiceSessionCount > 0) ||
        (rawData?.practiceComparison && rawData.practiceComparison.hasPracticeHistory === true && (rawData.practiceComparison.practiceCount ?? 0) > 0) ||
        (rawData?.ai_summary?.practiceComparison && rawData.ai_summary.practiceComparison.hasPracticeHistory === true && (rawData.ai_summary.practiceComparison.practiceCount ?? 0) > 0) ||
        (comparisonData && comparisonData.hasPracticeHistory === true && (comparisonData.practiceCount ?? 0) > 0)
      )
    );
    const hasPracticeVsReality = Boolean(
      isCalendarMeeting &&
      hasPracticeHistory &&
      !meeting.isScenario && !meeting.isPitch && !meeting.isDigitalTwin
    );
    if (activeTab === 'meeting_notes' && !hasNotes) {
      setActiveTab('ai_summary');
    } else if (activeTab === 'transcript' && !hasTrans) {
      setActiveTab('ai_summary');
    } else if (activeTab === 'practice_vs_reality' && !hasPracticeVsReality) {
      setActiveTab('ai_summary');
    }
  }, [meeting, rawData, activeTab, comparisonData]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true); setError(null);
        if (!meetingId) { setError('Meeting not found.'); return; }
        const state = (location.state as any) || {};
        const stateMeeting = state.meeting;
        const stateMeetingId = state.meetingId || state.id || stateMeeting?.id || stateMeeting?._id || stateMeeting?.meetingId;
        let hasStateData = false;

        if (stateMeeting && String(stateMeetingId) === String(meetingId)) {
          const transformed = transform(stateMeeting);
          setMeeting(transformed);
          setRawData(stateMeeting);
          hasStateData = true;
          // Pitches, scenarios, digital twin sessions do not need /v1/meeting API fetch
          if (transformed.isPitch || transformed.isScenario || transformed.isDigitalTwin) {
            setLoading(false);
            return;
          }
        }

        try {
          const isAdmin = role === 'ADMIN' || role === 'SUPER_ADMIN';
          let rawResponse: any = null;
          try {
            rawResponse = isAdmin ? await getAdminMeetingById(meetingId) : await getMeetingById(meetingId);
          } catch (e1) {
            if (isAdmin) {
              try {
                rawResponse = await getMeetingById(meetingId);
              } catch (e2) {
                rawResponse = null;
              }
            }
          }
          const raw = rawResponse && typeof rawResponse === 'object' && 'data' in rawResponse ? (rawResponse as any).data : rawResponse;
          if (raw && ((raw as any).id || (raw as any)._id || (raw as any).meetingId || (raw as any).legacyMongoId || (raw as any).sessionId)) {
            setRawData(raw);
            setMeeting(transform(raw as any));
          } else if (!hasStateData) {
            setError('Meeting not found.');
          }
        } catch (apiErr) {
          console.warn('Meeting API fetch failed, falling back to state data if available:', apiErr);
          if (!hasStateData) {
            setError('Meeting not found.');
          }
        }
      } catch (e) {
        console.error(e);
        setError('Failed to load meeting details. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [meetingId, role, location.state]);

  const handleBack = useCallback(() => {
    const st = (location.state as any) || {};
    const returnState = {
      page: st.page,
      selectedRep: st.selectedRep || st.rep,
      repTab: st.repTab || st.tab,
    };
    const candidate = st?.from || st?.returnTo || st?.fromPath || st?.prevPath || st?.backPath;
    if (candidate) {
      try {
        if (typeof candidate === 'string') {
          navigate(candidate, { state: returnState });
          return;
        }
        if (typeof candidate === 'object' && candidate.pathname) {
          navigate(candidate.pathname, { state: { ...(candidate.state || {}), ...returnState } });
          return;
        }
      } catch (e) {
        // if navigation by path fails, fall through to history back
      }
    }
    if (window.history && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/meeting-dashboard', { state: returnState });
    }
  }, [location.state, navigate]);

  const handleTimeUpdate = useCallback((t: number) => setCurrentTime(t), []);
  const handleSeek = useCallback((t: number) => {
    const el = audioRef.current;
    if (el) { el.currentTime = t; el.play().catch(() => { }); }
    setCurrentTime(t);
  }, []);

  const talkRatioData = useMemo(() => {
    if (!meeting) return { agent: 60, customer: 40 };
    const apiRatio = meeting.conversationAnalyticsRaw?.talk_listen_ratio;
    if (apiRatio && typeof apiRatio.talk === 'number') {
      if (typeof apiRatio.total === 'number' && apiRatio.total > 0) {
        const agentPct = Math.round((apiRatio.talk / apiRatio.total) * 100);
        return { agent: agentPct, customer: 100 - agentPct };
      }
      if (typeof apiRatio.listen === 'number') {
        const total = apiRatio.talk + apiRatio.listen;
        if (total > 0) {
          const agentPct = Math.round((apiRatio.talk / total) * 100);
          return { agent: agentPct, customer: 100 - agentPct };
        }
      }
    }
    return computeTalkRatio(meeting.transcriptJson, meeting.talkRatio);
  }, [meeting]);

  if (loading) return (
    <main style={{ flex: 1, minHeight: '100vh', background: colors.bg, marginTop: 60, display: 'flex', flexDirection: 'column' }} className={isDark ? "" : "light-theme-dashboard"}>
      <Shimmer />
    </main>
  );

  if (error || !meeting) return (
    <main style={{ flex: 1, minHeight: '100vh', background: colors.bg, marginTop: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} className={isDark ? "" : "light-theme-dashboard"}>
      <div style={{ borderRadius: 16, border: '1px solid rgba(241,114,114,0.2)', background: 'rgba(241,114,114,0.06)', padding: '24px 32px', textAlign: 'center' }}>
        <p style={{ color: '#F17272', fontSize: 16, marginBottom: 16 }}>{error || 'Meeting not found'}</p>
        <button onClick={handleBack}
          style={{ fontSize: 16, border: '1px solid rgba(241,114,114,0.3)', borderRadius: 10, padding: '8px 20px', color: '#F17272', background: 'none', cursor: 'pointer' }}>
          Go back
        </button>
      </div>
    </main>
  );

  const sc = getSemanticColor(meeting.aiScore.overall);
  const dateStr = meeting.createdAt ? new Date(meeting.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const hasAudio = (() => {
    if (!meeting?.audioUrl) return false;
    const lowerUrl = meeting.audioUrl.toLowerCase();
    const docExtensions = ['.docx', '.doc', '.pdf', '.xls', '.xlsx', '.txt', '.csv', '.ppt', '.pptx', '.zip', '.rar'];
    const hasDocExtension = docExtensions.some(ext => lowerUrl.endsWith(ext) || lowerUrl.includes(ext + '?') || lowerUrl.includes(ext + '&'));
    return !hasDocExtension;
  })();
  const hasTranscript = meeting.transcriptJson.length > 0 || !!meeting.transcript_text.trim();

  return (
    <main style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', background: colors.bg }} className={isDark ? "" : "light-theme-dashboard"}>

      {/* ── HEADER BAR ── */}
      <div style={{ flexShrink: 0, borderBottom: `1px solid ${colors.border}`, background: colors.surface, padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <button onClick={handleBack}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 10, border: `1px solid ${colors.border}`, background: colors.surfaceAlt, color: colors.text, fontSize: 16, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
          <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M11 17l-5-5m0 0l5-5m-5 5h12" /></svg>
          Back
        </button>

        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, overflow: 'hidden' }}>
          <div style={{ width: 38, height: 38, borderRadius: '50%', background: isDark ? `${ACCENT}22` : '#5a9d5018', border: `2px solid ${isDark ? `${ACCENT}55` : '#5a9d5055'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 17, fontWeight: 600, color: isDark ? ACCENT : '#5a9d50' }}>
            {meeting.salesRep.charAt(0).toUpperCase()}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontSize: 18, fontWeight: 600, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meeting.salesRep}</span>
            {meeting.company && <span style={{ fontSize: 16, color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meeting.company}{meeting.industry ? ` · ${meeting.industry}` : ''}</span>}
          </div>

          <div style={{ width: 1, height: 24, background: colors.border, flexShrink: 0, marginLeft: 4 }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ fontSize: 22, fontWeight: 600, color: sc }}>{meeting.aiScore.overall}%</span>
            <span style={{ fontSize: 18, color: sc, fontWeight: 600 }}>{getStatusLabel(meeting.aiScore.overall)}</span>
          </div>

          {dateStr && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: colors.textMuted, fontSize: 17, flexShrink: 0 }}>
              <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><path strokeLinecap="round" d="M16 2v4M8 2v4M3 10h18" /></svg>
              {dateStr}
            </div>
          )}

          <span style={{ fontSize: 18, fontWeight: 600, background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 6, padding: '3px 9px', color: colors.text, textTransform: 'capitalize', flexShrink: 0 }}>
            {meeting.isScenario ? 'Scenario' : meeting.meetingType}
          </span>
          <span style={{ fontSize: 18, fontWeight: 700, background: meeting.status === 'Active' ? (isDark ? `${ACCENT}1f` : '#5a9d5018') : colors.surfaceAlt, border: `1px solid ${meeting.status === 'Active' ? (isDark ? `${ACCENT}55` : '#5a9d5055') : colors.border}`, borderRadius: 6, padding: '3px 9px', color: meeting.status === 'Active' ? (isDark ? ACCENT : '#5a9d50') : colors.text, flexShrink: 0 }}>
            {meeting.status}
          </span>
        </div>

        <MeetingPDFReport meeting={meeting as any} rawData={rawData || meeting} talkRatioData={talkRatioData} isDark={isDark} />
      </div>

      {/* ── BODY ── */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflow: 'hidden', padding: '14px 24px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>

          {hasAudio && (
            <AudioPlayer url={meeting.audioUrl} downloadUrl={meeting.audioDownloadUrl} audioRef={audioRef as React.RefObject<HTMLAudioElement>} onTimeUpdate={handleTimeUpdate} initialDuration={meeting.duration} />
          )}

          {(() => {
            const meetingNotesRaw = meeting.meetingNotes as any;
            const hasMeetingNotes = Boolean(
              meetingNotesRaw &&
              (typeof meetingNotesRaw === 'string'
                ? (meetingNotesRaw as string).trim().length > 0
                : typeof meetingNotesRaw === 'object' &&
                Object.keys(meetingNotesRaw).length > 0 &&
                (meetingNotesRaw.executiveSummary ||
                  meetingNotesRaw.title ||
                  (meetingNotesRaw.keyDiscussionPoints && meetingNotesRaw.keyDiscussionPoints.length > 0) ||
                  (meetingNotesRaw.actionItems && meetingNotesRaw.actionItems.length > 0) ||
                  (meetingNotesRaw.nextSteps && meetingNotesRaw.nextSteps.length > 0) ||
                  (meetingNotesRaw.openQuestions && meetingNotesRaw.openQuestions.length > 0)))
            );

            const isCalendarMeeting = Boolean(
              String(meeting.legacyMongoId || rawData?.legacyMongoId || '').startsWith('cal-') ||
              meetingNotesRaw?.isCalendarEvent === true ||
              meetingNotesRaw?.provider === 'google' ||
              meetingNotesRaw?.provider === 'outlook' ||
              meetingNotesRaw?.provider === 'teams' ||
              meetingNotesRaw?.provider === 'microsoft' ||
              meetingNotesRaw?.provider === 'office365' ||
              String(meeting.meetingType || rawData?.meeting_type || rawData?.meetingType || '').toLowerCase().includes('teams') ||
              String(meeting.meetingType || rawData?.meeting_type || rawData?.meetingType || '').toLowerCase().includes('meet') ||
              String(meeting.meetingType || rawData?.meeting_type || rawData?.meetingType || '').toLowerCase().includes('zoom')
            );

            const hasPracticeHistory = Boolean(
              isCalendarMeeting && (
                (meeting.hasPracticeSessions === true && (meeting.practiceSessionCount ?? 1) > 0) ||
                (typeof meeting.practiceSessionCount === 'number' && meeting.practiceSessionCount > 0) ||
                (meetingNotesRaw?.hasPracticeSessions === true && (meetingNotesRaw?.practiceSessionCount ?? 1) > 0) ||
                (typeof meetingNotesRaw?.practiceSessionCount === 'number' && meetingNotesRaw.practiceSessionCount > 0) ||
                (rawData?.hasPracticeSessions === true && (rawData?.practiceSessionCount ?? 1) > 0) ||
                (typeof rawData?.practiceSessionCount === 'number' && rawData.practiceSessionCount > 0) ||
                (rawData?.practiceComparison && rawData.practiceComparison.hasPracticeHistory === true && (rawData.practiceComparison.practiceCount ?? 0) > 0) ||
                (rawData?.ai_summary?.practiceComparison && rawData.ai_summary.practiceComparison.hasPracticeHistory === true && (rawData.ai_summary.practiceComparison.practiceCount ?? 0) > 0) ||
                (comparisonData && comparisonData.hasPracticeHistory === true && (comparisonData.practiceCount ?? 0) > 0)
              )
            );

            const showPracticeVsRealityTab = Boolean(
              isCalendarMeeting &&
              hasPracticeHistory &&
              !meeting.isScenario && !meeting.isPitch && !meeting.isDigitalTwin
            );

            const availableTabs = [
              hasTranscript ? { key: 'transcript' as const, label: 'Transcript', icon: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h10" /></svg> } : null,
              { key: 'ai_summary' as const, label: 'AI Analysis', icon: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg> },
              hasMeetingNotes ? { key: 'meeting_notes' as const, label: 'Meeting Notes', icon: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><line x1="10" y1="9" x2="8" y2="9" /></svg> } : null,
              showPracticeVsRealityTab ? { key: 'practice_vs_reality' as const, label: 'Practice vs Reality', icon: <Brain className="w-3.5 h-3.5" /> } : null,
            ].filter(Boolean) as { key: 'transcript' | 'ai_summary' | 'meeting_notes' | 'practice_vs_reality'; label: string; icon: React.ReactNode }[];

            return (
              <>
                {availableTabs.length > 1 && (
                  <div style={{ flexShrink: 0 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${availableTabs.length}, 1fr)`, gap: 4, borderRadius: 12, border: `1px solid ${colors.border}`, background: colors.surfaceAlt, padding: 4 }}>
                      {availableTabs.map((tab) => {
                        const active = activeTab === tab.key;
                        return (
                          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                              borderRadius: 9, padding: '10px 18px', fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer',
                              transition: 'all 0.2s', background: active ? (isDark ? '#B8F890' : '#5a9d50') : 'transparent', color: active ? (isDark ? '#0a1608' : '#ffffff') : colors.textMuted,
                            }}>
                            <span style={{ opacity: active ? 1 : 0.6, color: active ? (isDark ? '#0a1608' : '#ffffff') : 'inherit' }}>{tab.icon}</span>
                            {tab.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  {activeTab === 'meeting_notes' && hasMeetingNotes ? (
                    <div style={{ height: '100%', overflowY: 'auto' }}>
                      <MeetingNotesPanel meetingNotes={meeting.meetingNotes} colors={colors} isDark={isDark} />
                    </div>
                  ) : activeTab === 'transcript' && hasTranscript ? (
                    <div style={{ borderRadius: 16, border: `1px solid ${colors.border}`, background: colors.surface, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <TranscriptPanel
                        segments={meeting.transcriptJson}
                        transcript_text={meeting.transcript_text}
                        currentTime={currentTime}
                        hasTiming={meeting.hasTiming && hasAudio}
                        onSeek={handleSeek}
                      />
                    </div>
                  ) : activeTab === 'practice_vs_reality' && showPracticeVsRealityTab ? (
                    <div style={{ height: '100%', overflowY: 'auto' }}>
                      <PracticeGrowthView meetingId={meeting.id} realData={rawData || meeting} />
                    </div>
                  ) : (
                    <div style={{ height: '100%', overflowY: 'auto' }}>
                      <AIAnalysisPanel meeting={meeting} />
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </main>
  );
};

export default MeetingDetailPage;